'use client';

import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { encodePassphrase, randomString } from '@/lib/client-utils';
import styles from '../styles/Home.module.css';

export default function Page() {
  const router = useRouter();
  const [organizationCode, setOrganizationCode] = useState('');
  const [e2ee, setE2ee] = useState(false);
  const [sharedPassphrase, setSharedPassphrase] = useState(randomString(64));
  const [meetingUrl, setMeetingUrl] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const createMeeting = async () => {
    setCreating(true);
    setError('');
    try {
      const response = await fetch('/api/rooms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organizationCode, encrypted: e2ee }),
      });
      const data = (await response.json()) as { meetingUrl?: string; error?: string };
      if (!response.ok || !data.meetingUrl) {
        throw new Error(data.error || 'Не удалось создать встречу');
      }
      const publicMeetingUrl = new URL(data.meetingUrl, window.location.origin).toString();
      const url = e2ee
        ? `${publicMeetingUrl}#${encodePassphrase(sharedPassphrase)}`
        : publicMeetingUrl;
      setMeetingUrl(url);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось создать встречу');
    } finally {
      setCreating(false);
    }
  };

  const copyMeetingUrl = async () => navigator.clipboard.writeText(meetingUrl);

  return (
    <main className={styles.main} data-lk-theme="default">
      <div className="header">
        <h1 style={{ margin: 0, fontSize: '3rem', letterSpacing: '-0.06em' }}>OceanBox</h1>
        <h2>Защищённые видеовстречи на вашем сервере</h2>
      </div>

      <div className={styles.tabContent}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label htmlFor="organization-code">Код организации</label>
          <input
            id="organization-code"
            type="password"
            autoComplete="current-password"
            value={organizationCode}
            onChange={(event) => setOrganizationCode(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && void createMeeting()}
            placeholder="Введите код доступа"
          />
        </div>

        <button
          style={{ width: '100%', paddingInline: '1.25rem' }}
          className="lk-button"
          onClick={() => void createMeeting()}
          disabled={creating || organizationCode.length === 0}
        >
          {creating ? 'Создаём…' : 'Создать встречу'}
        </button>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <input
              id="use-e2ee"
              type="checkbox"
              checked={e2ee}
              onChange={(event) => setE2ee(event.target.checked)}
            />
            <label htmlFor="use-e2ee">Сквозное шифрование</label>
          </div>

          <p style={{ margin: 0 }}>
            {e2ee
              ? 'При сквозном шифровании серверная запись и расшифровка недоступны.'
              : 'Встреча записывается для расшифровки. Предупредите участников перед началом.'}
          </p>

          {e2ee && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label htmlFor="passphrase">Пароль встречи</label>
              <input
                id="passphrase"
                type="password"
                value={sharedPassphrase}
                onChange={(event) => setSharedPassphrase(event.target.value)}
              />
            </div>
          )}
        </div>

        {error && <p style={{ color: '#ff6b6b', margin: 0 }}>{error}</p>}

        {meetingUrl && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <label htmlFor="meeting-url">Ссылка для всех участников</label>
            <input id="meeting-url" readOnly value={meetingUrl} onFocus={(e) => e.target.select()} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <button className="lk-button" onClick={() => void copyMeetingUrl()}>
                Копировать
              </button>
              <button className="lk-button" onClick={() => router.push(meetingUrl)}>
                Войти
              </button>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
